import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRequirementById, updateRequirement } from '../../store/slices/hrSlice';
import { fetchCandidates } from '../../store/slices/hrSlice';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import StatusBadge from '../../components/StatusBadge';
import CommentSection from '../../components/CommentSection';
import { HR_REQUIREMENT_STATUS, COMMENT_ENTITY_TYPES } from '../../utils/constants';
import { formatDateTime } from '../../utils/formatDate';

const RequirementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentRequirement, candidates, loading } = useSelector((state) => state.hr);
  const [editing, setEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (id && id !== 'new') {
      dispatch(fetchRequirementById(id));
      dispatch(fetchCandidates({ requirement_id: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentRequirement) {
      setFormData({
        title: currentRequirement.title,
        department: currentRequirement.department,
        experience_required: currentRequirement.experience_required,
        number_of_openings: currentRequirement.number_of_openings,
        status: currentRequirement.status
      });
    }
  }, [currentRequirement]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      await dispatch(updateRequirement({ id, data: formData })).unwrap();
      setEditing(false);
    } catch (error) {
      alert('Failed to update requirement: ' + error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading || !currentRequirement) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid">
        <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/hr')}>
          <i className="ti ti-arrow-left me-1" />
          Back to Dashboard
        </button>

        <div className="card mb-3">
          <div className="card-body">
            {editing ? (
              <form onSubmit={handleUpdate}>
                <h2 className="fs-5 mb-3">Edit Requirement</h2>

                <div className="mb-3">
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    name="title"
                    className="form-control"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Department *</label>
                  <input
                    type="text"
                    name="department"
                    className="form-control"
                    value={formData.department}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Experience Required *</label>
                  <input
                    type="text"
                    name="experience_required"
                    className="form-control"
                    value={formData.experience_required}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Number of Openings *</label>
                  <input
                    type="number"
                    name="number_of_openings"
                    className="form-control"
                    value={formData.number_of_openings}
                    onChange={handleChange}
                    min="1"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    {Object.values(HR_REQUIREMENT_STATUS).map((status) => (
                      <option key={status} value={status}>
                        {status.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setEditing(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={updating}>
                    {updating ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <h2 className="fs-4 mb-2">{currentRequirement.title}</h2>
                    <StatusBadge status={currentRequirement.status} />
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-primary" onClick={() => setEditing(true)}>
                      Edit
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate(`/hr/requirements/${id}/candidates/new`)}
                    >
                      <i className="ti ti-plus me-1" />
                      Add Candidate
                    </button>
                  </div>
                </div>

                <div className="row g-3 mt-3">
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">DEPARTMENT</label>
                    <p className="mb-0">{currentRequirement.department}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">EXPERIENCE REQUIRED</label>
                    <p className="mb-0">{currentRequirement.experience_required}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">NUMBER OF OPENINGS</label>
                    <p className="mb-0">{currentRequirement.number_of_openings}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">ASSIGNED HR</label>
                    <div className="d-flex gap-1 flex-wrap mt-1">
                      {(Array.isArray(currentRequirement.assigned_hr) ? currentRequirement.assigned_hr : [currentRequirement.assigned_hr]).filter(Boolean).map((emp, idx) => (
                        <span key={idx} className="badge bg-primary" style={{ fontSize: '11px' }}>
                          {emp.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Candidates Section */}
        <div className="card table-responsive mb-3">
          <div className="card-header bg-white">
            <h3 className="fs-6 mb-0">Candidates</h3>
          </div>
          <table className="table mb-0 text-nowrap table-hover">
            <thead className="table-light border-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Experience</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4 text-muted">
                    No candidates yet
                  </td>
                </tr>
              ) : (
                candidates.map((candidate) => (
                  <tr key={candidate._id} className="align-middle">
                    <td className="fw-medium">{candidate.name}</td>
                    <td>{candidate.email}</td>
                    <td>{candidate.phone}</td>
                    <td>{candidate.experience}</td>
                    <td><StatusBadge status={candidate.status} /></td>
                    <td>{formatDateTime(candidate.created_at)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/hr/candidates/${candidate._id}`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <CommentSection
          entityType={COMMENT_ENTITY_TYPES.HR_REQUIREMENT}
          entityId={id}
        />
      </div>
    </Layout>
  );
};

export default RequirementDetail;
